import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * A real product screenshot in a light browser frame. Screens live in
 * src/assets/product/ and are captured from a demo tenant (Maid Madness).
 */
export function ProductShot({
  src,
  alt,
  url = 'app.thepolishedversion.com/admin',
  width,
  height,
  className,
  imgClassName,
  testId,
}: {
  src: string;
  alt: string;
  url?: string;
  width: number;
  height: number;
  className?: string;
  imgClassName?: string;
  testId?: string;
}) {
  return (
    <figure
      className={cn(
        'overflow-hidden rounded-[1.25rem] border border-[hsl(var(--border))] bg-white shadow-[0_40px_80px_-36px_rgba(20,16,70,0.5)]',
        className
      )}
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 border-b border-[hsl(var(--border))] bg-[hsl(var(--mk-panel))] px-4 py-2.5" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--mk-muted)/0.3)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--mk-muted)/0.3)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--mk-muted)/0.3)]" />
        <span className="mx-auto truncate rounded-full bg-white px-4 py-0.5 text-[11px] text-[hsl(var(--mk-muted))]">{url}</span>
      </div>
      <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async" className={cn('block h-auto w-full', imgClassName)} />
    </figure>
  );
}

/** Notification-style card that fades in and out on a loop (see .mk-toast). */
export function ProductToast({
  className,
  delay,
  icon,
  tint,
  title,
  body,
}: {
  className?: string;
  delay: number;
  icon: ReactNode;
  tint: string;
  title: string;
  body: string;
}) {
  return (
    <div
      className={cn(
        'mk-toast absolute z-20 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-[hsl(var(--mk-ink))] shadow-[0_20px_40px_-16px_rgba(10,8,40,0.55)]',
        className
      )}
      style={{ ['--mk-delay' as string]: `${delay}ms` }}
      aria-hidden
    >
      <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--mk-ink))]', tint)}>{icon}</span>
      <span className="leading-tight">
        <span className="block font-display text-sm font-semibold">{title}</span>
        <span className="block text-xs text-[hsl(var(--mk-muted))]">{body}</span>
      </span>
    </div>
  );
}
